// API service layer for communicating with the backend
// Use environment variable for production, fallback to localhost for development

import type { RequestOptions, TrainerUpdateData, CheckoutSessionPayload } from "../types";

const API_BASE_URL: string = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

interface AvailabilityData {
  available_from?: string;
  available_until?: string;
}

class ApiService {
  private token: string | null;

  constructor() {
    this.token = null;
  }

  setToken(token: string): void {
    this.token = token;
  }

  clearToken(): void {
    this.token = null;
  }

  // Generic request method
  async request<T = Record<string, unknown>>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log("=== API REQUEST ===");
    console.log("URL:", url);
    console.log("Options:", options);

    const config: RequestInit & { headers: Record<string, string> } = {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    };

    // Add authorization header if token exists
    if (this.token) {
      config.headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      console.log("Making fetch request to:", url);
      const response = await fetch(url, config);
      console.log("Response status:", response.status);
      const data = await response.json();
      console.log("Response data:", data);

      if (!response.ok) {
        throw new Error(data.detail || data.Message || `HTTP error! status: ${response.status}`);
      }

      return data as T;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  // Client API methods
  async createClient(clientData: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/client/createClient", {
      method: "POST",
      body: JSON.stringify(clientData),
    });
  }

  async getClientProfile(clientId: string): Promise<Record<string, unknown>> {
    return this.request(`/client/profile/${clientId}`, {
      method: "GET",
    });
  }

  async deleteClient(clientId: string): Promise<Record<string, unknown>> {
    return this.request("/client/deleteClient", {
      method: "DELETE",
      body: JSON.stringify({ client_id: clientId }),
    });
  }

  // Pet API methods
  async createPet(petData: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/client/pet/createClientPet", {
      method: "POST",
      body: JSON.stringify(petData),
    });
  }

  async getClientPets(clientId: string): Promise<Record<string, unknown>> {
    return this.request(`/client/pet/getClientPets?client_id=${encodeURIComponent(clientId)}`, {
      method: "POST",
    });
  }

  async getProfile(clientId: string): Promise<Record<string, unknown>> {
    return this.request(`/client/getProfileById?client_id=${encodeURIComponent(clientId)}`, {
      method: "POST",
    });
  }

  async updateClientProfile(
    clientId: string,
    profileData: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/client/profile/${clientId}`, {
      method: "PUT",
      body: JSON.stringify(profileData),
    });
  }

  async getClientSessions(clientId: string): Promise<Record<string, unknown>> {
    return this.request(`/client/sessions/${clientId}`, {
      method: "GET",
    });
  }

  async updatePet(
    petId: string,
    petData: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/client/pet/updateClientPet?pet_id=${encodeURIComponent(petId)}`, {
      method: "PUT",
      body: JSON.stringify(petData),
    });
  }

  async deletePet(petId: string): Promise<Record<string, unknown>> {
    return this.request(
      `/client/pet/deleteClientPet?pet_id=${encodeURIComponent(petId)}`,
      { method: "DELETE" },
    );
  }

  // Session request methods
  async createSessionRequest(
    requestData: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    console.log("=== API CALL: createSessionRequest ===");
    console.log("Request data:", requestData);

    return this.request("/session/createRequest", {
      method: "POST",
      body: JSON.stringify(requestData),
    });
  }

  async getActiveTrainingRequests(clientId: string): Promise<Record<string, unknown>> {
    return this.request(`/session/getActiveRequests?client_id=${encodeURIComponent(clientId)}`, {
      method: "GET",
    });
  }

  async createCheckoutSession(
    requestIdOrPayload: string | CheckoutSessionPayload,
  ): Promise<Record<string, unknown>> {
    const payload =
      typeof requestIdOrPayload === "object"
        ? requestIdOrPayload
        : { request_id: requestIdOrPayload };
    return this.request("/payment/createCheckoutSession", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async verifyCheckoutSession(
    verifyData: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/payment/verifySession", {
      method: "POST",
      body: JSON.stringify(verifyData),
    });
  }

  async cancelSessionRequest(requestId: string): Promise<Record<string, unknown>> {
    return this.request(`/session/cancelRequest?request_id=${encodeURIComponent(requestId)}`, {
      method: "DELETE",
    });
  }

  async registerSessionJoin(requestId: string, role: string): Promise<Record<string, unknown>> {
    return this.request("/session/registerJoin", {
      method: "POST",
      body: JSON.stringify({ request_id: requestId, role }),
    });
  }

  // Trainer API methods
  async createTrainer(trainerData: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/trainer/createTrainer", {
      method: "POST",
      body: JSON.stringify(trainerData),
    });
  }

  async submitBackgroundCheck(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/trainer/submitBackgroundCheck", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getTrainerID(email: string): Promise<Record<string, unknown>> {
    console.log("=== API CALL: getTrainerID ===");
    console.log("Email:", email);
    console.log("URL:", `/trainer/getTrainerID?trainer_email=${email}`);
    return this.request(`/trainer/getTrainerID?trainer_email=${email}`, {
      method: "POST",
    });
  }

  async getTrainerProfile(trainerId: string): Promise<Record<string, unknown>> {
    return this.request(`/trainer/profile/${trainerId}`, {
      method: "GET",
    });
  }

  async createSupportTicket(ticketData: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/support/tickets", {
      method: "POST",
      body: JSON.stringify(ticketData),
    });
  }

  async editTrainer(
    trainerId: string,
    updateData: TrainerUpdateData,
  ): Promise<Record<string, unknown>> {
    // Convert the updateData to the format expected by the backend
    const params = new URLSearchParams();
    params.append("trainer_id", trainerId);

    // Map frontend field names to backend parameter names
    if (updateData.first_name) params.append("trainer_first_name", updateData.first_name);
    if (updateData.last_name) params.append("trainer_last_name", updateData.last_name);
    if (updateData.email) params.append("trainer_email", updateData.email);
    if (updateData.phone_number) params.append("trainer_phone_number", updateData.phone_number);
    if (updateData.address) params.append("trainer_address", updateData.address);
    if (updateData.specialties) params.append("specialties", updateData.specialties);
    if (updateData.experience_years) params.append("experience_years", updateData.experience_years);
    if (updateData.hourly_rate) params.append("hourly_rate", updateData.hourly_rate);
    if (updateData.bio) params.append("bio", updateData.bio);

    console.log("Edit trainer URL:", `/trainer/editTrainer?${params.toString()}`);
    console.log("Edit trainer params:", params.toString());

    return this.request(`/trainer/editTrainer?${params.toString()}`, {
      method: "PUT",
    });
  }

  async deleteTrainer(trainerId: string): Promise<Record<string, unknown>> {
    return this.request("/trainer/deleteTrainer", {
      method: "DELETE",
      body: JSON.stringify({ trainer_id: trainerId }),
    });
  }

  async deleteUserByUsername(username: string): Promise<Record<string, unknown>> {
    return this.request(`/admin/deleteUserByUsername?username=${encodeURIComponent(username)}`, {
      method: "DELETE",
    });
  }

  async pushTrainerReview(reviewData: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/trainer/pushReview", {
      method: "POST",
      body: JSON.stringify(reviewData),
    });
  }

  async submitSessionReview(reviewData: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/session/submitReview", {
      method: "POST",
      body: JSON.stringify(reviewData),
    });
  }

  async getSessionRating(sessionRequestId: string): Promise<Record<string, unknown>> {
    return this.request(
      `/session/getRating?session_request_id=${encodeURIComponent(sessionRequestId)}`,
      {
        method: "GET",
      },
    );
  }

  async getTrainerScore(trainerId: string): Promise<Record<string, unknown>> {
    console.log("=== API CALL: getTrainerScore ===");
    console.log("Trainer ID:", trainerId);
    console.log("URL:", `/trainer/getScore?trainer_id=${trainerId}`);
    return this.request(`/trainer/getScore?trainer_id=${trainerId}`, {
      method: "GET",
    });
  }

  async updateTrainerProfile(
    trainerId: string,
    profileData: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/trainer/profile/${trainerId}`, {
      method: "PUT",
      body: JSON.stringify(profileData),
    });
  }

  async adminLogin(username: string, password: string): Promise<Record<string, unknown>> {
    return this.request("/admin/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
  }

  async uploadTrainerDocument(
    trainerId: string,
    documentType: string,
    file: File,
  ): Promise<Record<string, unknown>> {
    const formData = new FormData();
    formData.append("file", file);

    return this.request(
      `/trainer/uploadDocument?trainer_id=${trainerId}&document_type=${documentType}`,
      {
        method: "POST",
        body: formData,
        headers: {}, // Let browser set Content-Type for FormData
      },
    );
  }

  async deleteTrainerDocument(
    trainerId: string,
    documentType: string,
  ): Promise<Record<string, unknown>> {
    return this.request(
      `/trainer/deleteDocument?trainer_id=${trainerId}&document_type=${documentType}`,
      {
        method: "DELETE",
      },
    );
  }

  // Session Management
  async createSession(sessionData: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/session/create", {
      method: "POST",
      body: JSON.stringify(sessionData),
    });
  }

  async updateSession(sessionData: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/session/update", {
      method: "PUT",
      body: JSON.stringify(sessionData),
    });
  }

  async updateSessionPayment(
    paymentData: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/session/payment", {
      method: "PUT",
      body: JSON.stringify(paymentData),
    });
  }

  async getSession(sessionId: string): Promise<Record<string, unknown>> {
    return this.request(`/session/${sessionId}`, {
      method: "GET",
    });
  }

  async generateInvoice(invoiceData: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request("/invoice/generate", {
      method: "POST",
      body: JSON.stringify(invoiceData),
    });
  }

  // Reference Video Management
  async uploadReferenceVideo(formData: FormData): Promise<Record<string, unknown>> {
    return this.request("/reference-video/upload", {
      method: "POST",
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  async getReferenceVideos(sessionId: string): Promise<Record<string, unknown>> {
    return this.request(`/reference-videos/${sessionId}`, {
      method: "GET",
    });
  }

  async deleteReferenceVideo(videoId: string): Promise<Record<string, unknown>> {
    return this.request(`/reference-video/${videoId}`, {
      method: "DELETE",
    });
  }

  // Trainer Payment Management
  async getTrainerPayments(trainerId: string): Promise<Record<string, unknown>> {
    return this.request(`/trainer/${trainerId}/payments`, {
      method: "GET",
    });
  }

  async getTrainerEarningsSummary(
    trainerId: string,
    year: number,
  ): Promise<Record<string, unknown>> {
    return this.request(`/trainer/${trainerId}/earnings-summary?year=${year}`, {
      method: "GET",
    });
  }

  async processTrainerPayout(
    trainerId: string,
    payoutData: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/trainer/${trainerId}/payout`, {
      method: "POST",
      body: JSON.stringify(payoutData),
    });
  }

  // Admin Payment Management
  async getTrainer1099Reports(year: number): Promise<Record<string, unknown>> {
    return this.request(`/admin/trainer-1099-reports/${year}`, {
      method: "GET",
    });
  }

  // Session API methods
  async requestSession(clientId: string, trainerId: string): Promise<Record<string, unknown>> {
    return this.request("/session/requestSession", {
      method: "POST",
      body: JSON.stringify({ client_id: clientId, trainer_id: trainerId }),
    });
  }

  async acceptSession(sessionRequestId: string): Promise<Record<string, unknown>> {
    return this.request(
      `/session/acceptSession?session_request_id=${encodeURIComponent(sessionRequestId)}`,
      {
        method: "POST",
        body: JSON.stringify({ session_request_id: sessionRequestId }),
      },
    );
  }

  async declineSession(sessionRequestId: string): Promise<Record<string, unknown>> {
    return this.request("/session/declineSession", {
      method: "POST",
      body: JSON.stringify({ session_request_id: sessionRequestId }),
    });
  }

  async getSessionRequestDetails(requestId: string): Promise<Record<string, unknown>> {
    return this.request(`/session/getRequestDetails/${encodeURIComponent(requestId)}`, {
      method: "GET",
    });
  }

  async createOrUpdateDeliverables(
    deliverablesData: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/session/deliverables", {
      method: "POST",
      body: JSON.stringify(deliverablesData),
    });
  }

  async getDeliverables(sessionRequestId: string): Promise<Record<string, unknown>> {
    return this.request(`/session/deliverables/${encodeURIComponent(sessionRequestId)}`);
  }

  async updateSessionFromRequest(
    requestId: string,
    updateData: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request(`/session/updateFromRequest/${encodeURIComponent(requestId)}`, {
      method: "PUT",
      body: JSON.stringify(updateData),
    });
  }

  async startSession(
    sessionRequestId: string,
    notes: string = "",
  ): Promise<Record<string, unknown>> {
    return this.request("/session/startSession", {
      method: "POST",
      body: JSON.stringify({ session_request_id: sessionRequestId, notes }),
    });
  }

  async endSession(requestId: string, notes: string = ""): Promise<Record<string, unknown>> {
    return this.request(`/session/endSession?request_id=${encodeURIComponent(requestId)}`, {
      method: "POST",
      body: JSON.stringify({ notes }),
    });
  }

  async saveSessionNotes(requestId: string, notes: string): Promise<Record<string, unknown>> {
    return this.updateSessionFromRequest(requestId, { notes });
  }

  async getClientSessionHistory(clientId: string): Promise<Record<string, unknown>> {
    return this.request("/session/getClientSessionHistory", {
      method: "GET",
      body: JSON.stringify({ client_id: clientId }),
    });
  }

  // Get all trainers from backend
  async getAllTrainers(): Promise<Record<string, unknown>> {
    return this.request("/trainer/getAllTrainers", {
      method: "GET",
    });
  }

  // Get only active trainers from backend
  async getActiveTrainers(): Promise<Record<string, unknown>> {
    return this.request("/trainer/getActiveTrainers", {
      method: "GET",
    });
  }

  // Check trainer availability status
  async getTrainerAvailability(trainerId: string): Promise<Record<string, unknown>> {
    return this.request(
      `/session/getTrainerAvailability?trainer_id=${encodeURIComponent(trainerId)}`,
      {
        method: "GET",
      },
    );
  }

  // Make trainer active
  async makeTrainerActive(
    trainerId: string,
    availabilityData: AvailabilityData = {},
  ): Promise<Record<string, unknown>> {
    const params = new URLSearchParams();
    params.append("trainer_id", trainerId);
    if (availabilityData.available_from) {
      params.append("available_from", availabilityData.available_from);
    }
    if (availabilityData.available_until) {
      params.append("available_until", availabilityData.available_until);
    }

    console.log("=== API CALL: makeTrainerActive ===");
    console.log("Trainer ID:", trainerId);
    console.log("Availability Data:", availabilityData);
    console.log("URL:", `/session/makeTrainerActive?${params.toString()}`);

    return this.request(`/session/makeTrainerActive?${params.toString()}`, {
      method: "POST",
    });
  }

  // Make trainer inactive
  async makeTrainerInactive(trainerId: string): Promise<Record<string, unknown>> {
    const params = new URLSearchParams();
    params.append("trainer_id", trainerId);

    console.log("=== API CALL: makeTrainerInactive ===");
    console.log("Trainer ID:", trainerId);
    console.log("URL:", `/session/makeTrainerInactive?${params.toString()}`);

    return this.request(`/session/makeTrainerInactive?${params.toString()}`, {
      method: "POST",
    });
  }

  // Get trainer session requests
  async getTrainerRequests(trainerId: string): Promise<Record<string, unknown>> {
    console.log("=== API CALL: getTrainerRequests ===");
    console.log("Trainer ID:", trainerId);

    return this.request(`/trainer/${encodeURIComponent(trainerId)}/requests`, {
      method: "GET",
    });
  }

  // Authentication methods
  async requestEmailVerification(
    email: string,
    userType: string,
  ): Promise<Record<string, unknown>> {
    return this.request("/auth/request-email-verification", {
      method: "POST",
      body: JSON.stringify({ email, user_type: userType }),
    });
  }

  async verifyEmailCode(email: string, verificationCode: string): Promise<Record<string, unknown>> {
    return this.request("/auth/verify-email-code", {
      method: "POST",
      body: JSON.stringify({ email, verification_code: verificationCode }),
    });
  }

  async register(
    username: string,
    email: string,
    password: string,
    name: string = "",
    userType: string,
    verificationToken: string,
  ): Promise<Record<string, unknown>> {
    const requestData = {
      username,
      email,
      password,
      name,
      user_type: userType,
      verification_token: verificationToken,
    };
    console.log("API Service - Registration request data:", requestData);

    return this.request("/auth/register", {
      method: "POST",
      body: JSON.stringify(requestData),
    });
  }

  async login(username: string, password: string): Promise<Record<string, unknown>> {
    return this.request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
  }

  async setUserType(userType: string): Promise<Record<string, unknown>> {
    return this.request("/auth/set-user-type", {
      method: "POST",
      body: JSON.stringify({ user_type: userType }),
    });
  }

  async getCurrentUser(): Promise<Record<string, unknown>> {
    return this.request("/auth/me", {
      method: "GET",
    });
  }

  async logout(): Promise<Record<string, unknown>> {
    return this.request("/auth/logout", {
      method: "POST",
    });
  }

  async forgotPassword(email: string): Promise<Record<string, unknown>> {
    return this.request("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<Record<string, unknown>> {
    return this.request("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  }

  async handleGoogleCallback(code: string): Promise<Record<string, unknown>> {
    return this.request("/auth/google/callback", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  }

  // Photo upload methods
  async uploadTrainerProfilePhoto(trainerId: string, file: File): Promise<Record<string, unknown>> {
    const formData = new FormData();
    formData.append("file", file);

    return this.request(`/trainer/uploadProfilePhoto?trainer_id=${trainerId}`, {
      method: "POST",
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  async uploadClientProfilePhoto(clientId: string, file: File): Promise<Record<string, unknown>> {
    const formData = new FormData();
    formData.append("file", file);

    return this.request(`/client/uploadProfilePhoto?client_id=${clientId}`, {
      method: "POST",
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  async uploadPetPhoto(petId: string, file: File): Promise<Record<string, unknown>> {
    const formData = new FormData();
    formData.append("file", file);

    return this.request(`/client/uploadPetPhoto?pet_id=${petId}`, {
      method: "POST",
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  // Trainer setup options methods
  async getTrainingPhilosophyOptions(): Promise<Record<string, unknown>> {
    return this.request("/trainer/options/training-philosophy", {
      method: "GET",
    });
  }

  async getAreasOfExpertiseOptions(): Promise<Record<string, unknown>> {
    return this.request("/trainer/options/areas-of-expertise", {
      method: "GET",
    });
  }

  async getCredentialsOptions(): Promise<Record<string, unknown>> {
    return this.request("/trainer/options/credentials", {
      method: "GET",
    });
  }

  async getLanguagesOptions(): Promise<Record<string, unknown>> {
    return this.request("/trainer/options/languages", {
      method: "GET",
    });
  }

  // Trainer registration payment methods
  async createTrainerRegistrationCheckout(
    data: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/payment/createTrainerRegistrationCheckout", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async verifyTrainerRegistrationPayment(
    data: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    return this.request("/payment/verifyTrainerRegistrationPayment", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Stripe Connect methods
  async createConnectAccount(trainerId: string): Promise<Record<string, unknown>> {
    return this.request("/payment/createConnectAccount", {
      method: "POST",
      body: JSON.stringify({ trainer_id: trainerId }),
    });
  }

  async createOnboardingLink(trainerId: string): Promise<Record<string, unknown>> {
    return this.request("/payment/createOnboardingLink", {
      method: "POST",
      body: JSON.stringify({ trainer_id: trainerId }),
    });
  }

  async getConnectAccountStatus(trainerId: string): Promise<Record<string, unknown>> {
    return this.request(`/payment/connectAccountStatus/${encodeURIComponent(trainerId)}`, {
      method: "GET",
    });
  }

  // Admin methods
  async getPendingTrainerApplications(): Promise<Record<string, unknown>> {
    return this.request("/admin/getPendingTrainerApplications", {
      method: "GET",
    });
  }

  async acceptTrainerApplication(trainerId: string): Promise<Record<string, unknown>> {
    return this.request("/admin/acceptTrainerApplication", {
      method: "POST",
      body: JSON.stringify({ trainer_id: trainerId }),
    });
  }

  async denyTrainerApplication(
    trainerId: string,
    reason: string = "",
  ): Promise<Record<string, unknown>> {
    return this.request("/admin/denyTrainerApplication", {
      method: "POST",
      body: JSON.stringify({ trainer_id: trainerId, reason }),
    });
  }

  async updateTrainerVerification(
    trainerId: string,
    updates: {
      insurance_verified?: boolean;
      insurance_expires_at?: string | null;
      credentials_verified?: boolean;
      background_check_verified?: boolean;
    },
  ): Promise<Record<string, unknown>> {
    return this.request(`/admin/trainer/${trainerId}/verification`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async getAllClients(): Promise<Record<string, unknown>> {
    return this.request("/admin/getAllClients", {
      method: "GET",
    });
  }

  async getAllSessions(trainerSearch: string | null = null): Promise<Record<string, unknown>> {
    let url = "/admin/getAllSessions";
    if (trainerSearch) {
      url += `?trainer_search=${encodeURIComponent(trainerSearch)}`;
    }
    return this.request(url, {
      method: "GET",
    });
  }

  async getAdminStats(
    startDate: string | null = null,
    endDate: string | null = null,
  ): Promise<Record<string, unknown>> {
    let url = "/admin/getStats";
    const params: string[] = [];
    if (startDate) params.push(`start_date=${encodeURIComponent(startDate)}`);
    if (endDate) params.push(`end_date=${encodeURIComponent(endDate)}`);
    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }
    return this.request(url, {
      method: "GET",
    });
  }

  async getAllTrainersForAdmin(): Promise<Record<string, unknown>> {
    return this.request("/admin/getAllTrainersForAdmin", {
      method: "GET",
    });
  }

  async getBilling(): Promise<Record<string, unknown>> {
    return this.request("/admin/getBilling", {
      method: "GET",
    });
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
